// frontend/src/components/staff/EditShiftModal.js
//
// Workforce — SCHED-SLICE1-E new/edit shift modal.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §3.4 +
// §6.3 (edit-after-publish info banner) + visual DoD §18.1 + §18.4.
//
// Triggered by:
//   - Click on empty cell (mode: 'new', prefilled with cell start)
//   - Drag-to-create on empty range (mode: 'new', prefilled with range)
//   - Click on shift block (mode: 'edit', loaded from shift)
//
// Pre-flight availability check runs on Save via FE-side HARD BLOCK
// policy (story Scenario 4 + visual spec §10.1 + scheduleBuilderUtils.
// preValidateAvailability). Weekly-cap is handled by the OUTER component
// via the modal's `onSave` callback returning the BE conflicts payload.
//
// Edit-after-publish info banner per visual spec §6.3 — informational
// only, does NOT block Save.

import React from "react";
import moment from "moment-timezone";
import {
  STUDIO_TZ,
  STUDIO_TZ_LABEL,
  ROLE_COLOR_MAP,
  formatRoleLabel,
  resolveStaffDisplayName,
  validateShiftFormPayload,
  preValidateAvailability,
  detectSameRoleOverlapShifts,
  formatBannerShiftTimeRange,
  formatBannerDayLabel,
  resolveStaffFirstName,
  firstNameFromConflictDescriptor,
} from "./scheduleBuilderUtils";
import AdminSetAvailabilityModal from "./AdminSetAvailabilityModal";
import AvailabilityPeekStrip from "./AvailabilityPeekStrip";
import "./EditShiftModal.css";

// SCHED-SAME-ROLE-OVERLAP-WARN — banner multi-overlap overflow threshold
// per visual spec §3.3: list up to 5 visible items; everything beyond
// collapses into "and X more" tail.
const SAME_ROLE_BANNER_MAX_VISIBLE_ROWS = 5;

const DEFAULT_DURATION_MINUTES = 60;

// Build 15-min time options for the start/end selects. Covers 24h.
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push({
        value: `${hh}:${mm}`,
        label: moment({ hour: h, minute: m }).format("h:mm A"),
      });
    }
  }
  return out;
})();

const formatHHMM = (isoOrDate) => {
  const m = moment.tz(isoOrDate, STUDIO_TZ);
  if (!m.isValid()) return "08:00";
  return m.format("HH:mm");
};

const formatDayYmd = (isoOrDate) => {
  const m = moment.tz(isoOrDate, STUDIO_TZ);
  if (!m.isValid()) return moment.tz(STUDIO_TZ).format("YYYY-MM-DD");
  return m.format("YYYY-MM-DD");
};

const composeIso = (dayYmd, hhmm) => {
  const [h, mm] = hhmm.split(":").map((n) => Number(n));
  return moment
    .tz(dayYmd, "YYYY-MM-DD", STUDIO_TZ)
    .startOf("day")
    .add(h || 0, "hours")
    .add(mm || 0, "minutes")
    .toISOString();
};

/**
 * SCHED-SAME-ROLE-OVERLAP-WARN — Soft-warning banner inside the New/Edit
 * Shift modal. Renders single-overlap or multi-overlap copy per visual
 * spec §3.3. Reuses PEEK §4 banner styling verbatim
 * (`edit-shift-modal__avail-banner` + `--soft` variant); only typographic
 * delta is the OTHER STAFF NAME bold emphasis (600 weight).
 */
const SameRoleOverlapBanner = ({
  descriptors,
  proposedStaffProfile,
  proposedDayIso,
  proposedRoleCode,
  isStackedBelowAvailBanner,
}) => {
  if (!Array.isArray(descriptors) || descriptors.length === 0) return null;

  const proposedFirstName =
    resolveStaffFirstName(proposedStaffProfile) || "This shift";
  const roleLabel = formatRoleLabel(proposedRoleCode);
  const dayLabel = formatBannerDayLabel(
    descriptors[0].startTime || proposedDayIso
  );
  const count = descriptors.length;
  const isSingle = count === 1;

  // Banner top margin tightens to 12px when stacked below PEEK §4 banner
  // (visual spec §7); default 16px when standalone.
  const stackedClass = isStackedBelowAvailBanner
    ? " edit-shift-modal__avail-banner--stacked"
    : "";

  const visibleRows = descriptors.slice(0, SAME_ROLE_BANNER_MAX_VISIBLE_ROWS);
  const overflowCount = Math.max(0, count - SAME_ROLE_BANNER_MAX_VISIBLE_ROWS);

  // Aria label encodes the overlap fact + the proceed-anyway hint per spec §9.
  const ariaLabel = isSingle
    ? `Same-role overlap detected. ${firstNameFromConflictDescriptor(
        descriptors[0]
      )} has a ${roleLabel} shift at ${formatBannerShiftTimeRange(
        descriptors[0]
      )} on ${dayLabel}. You can proceed; both shifts will be saved as parallel columns.`
    : `Same-role overlap detected. ${count} other ${roleLabel} shifts on ${dayLabel} overlap with this one. You can proceed; all shifts will be saved as parallel columns.`;

  const subLine = isSingle
    ? "Proceed anyway — both shifts will be saved and will render as parallel columns."
    : "Proceed anyway — all shifts will be saved and will render as parallel columns.";

  return (
    <div
      className={`edit-shift-modal__avail-banner edit-shift-modal__avail-banner--soft edit-shift-modal__same-role-banner${stackedClass}`}
      role="alert"
      aria-live="polite"
      aria-label={ariaLabel}
      data-testid="edit-shift-modal-same-role-overlap"
    >
      <div className="edit-shift-modal__avail-banner-row">
        <span
          className="edit-shift-modal__avail-banner-icon"
          aria-hidden="true"
        >
          ⚠
        </span>
        <div className="edit-shift-modal__avail-banner-text">
          {isSingle ? (
            <p
              className="edit-shift-modal__avail-banner-msg"
              data-testid="edit-shift-modal-same-role-overlap-primary"
            >
              {`${proposedFirstName} overlaps with `}
              <strong
                className="edit-shift-modal__same-role-banner-name"
                data-testid="edit-shift-modal-same-role-overlap-other-name"
              >
                {firstNameFromConflictDescriptor(descriptors[0])}
              </strong>
              {`'s ${roleLabel} shift on ${dayLabel} ${formatBannerShiftTimeRange(
                descriptors[0]
              )}.`}
            </p>
          ) : (
            <>
              <p
                className="edit-shift-modal__avail-banner-msg"
                data-testid="edit-shift-modal-same-role-overlap-primary"
              >
                {`${proposedFirstName} overlaps with ${count} ${roleLabel} shifts on ${dayLabel}:`}
              </p>
              <ul
                className="edit-shift-modal__same-role-banner-list"
                data-testid="edit-shift-modal-same-role-overlap-list"
              >
                {visibleRows.map((d) => (
                  <li
                    key={d._id || `${d.startTime}-${d.endTime}`}
                    className="edit-shift-modal__same-role-banner-list-item"
                  >
                    <strong className="edit-shift-modal__same-role-banner-name">
                      {firstNameFromConflictDescriptor(d)}
                    </strong>
                    {` — ${formatBannerShiftTimeRange(d)}`}
                  </li>
                ))}
                {overflowCount > 0 && (
                  <li
                    className="edit-shift-modal__same-role-banner-list-item edit-shift-modal__same-role-banner-list-overflow"
                    data-testid="edit-shift-modal-same-role-overlap-overflow"
                  >
                    {`and ${overflowCount} more`}
                  </li>
                )}
              </ul>
            </>
          )}
          <p
            className="edit-shift-modal__avail-banner-sub"
            data-testid="edit-shift-modal-same-role-overlap-sub"
          >
            {subLine}
          </p>
        </div>
      </div>
    </div>
  );
};

const EditShiftModal = ({
  open,
  mode, // 'new' | 'edit'
  initialShift, // { staffProfileId?, startTime, endTime, roleCode?, notes?, status?, _id? }
  weekDays, // array of { iso, weekday, monthLabel, dayLabel }
  staff, // array of StaffProfile records (from listShifts response)
  availability, // array of Availability records
  shifts, // SCHED-SAME-ROLE-OVERLAP-WARN — visible-week shifts from parent (TanStack-cached)
  staffById, // SCHED-SAME-ROLE-OVERLAP-WARN — id→profile map (already computed in parent via indexStaffById)
  onSave, // (payload) => Promise — caller handles BE call + bubble conflicts
  onDelete, // (shiftId) => void — DRAFT shifts only (DELETE /shifts/:id)
  onCancelPublished, // (shift) => void — PUBLISHED shifts; opens cancel-with-reason sub-modal (SCHED-SLICE1-C AC §14)
  onAvailabilityChanged, // SCHED-SLICE1-K — bubble to parent so it can refetch listShifts / conflict-detection after admin-set
  onClose,
  isSubmitting,
  serverErrorMessage,
}) => {
  const isEdit = mode === "edit";

  // ---- Form state ---------------------------------------------------------
  const [staffProfileId, setStaffProfileId] = React.useState("");
  const [dayYmd, setDayYmd] = React.useState("");
  const [startHHMM, setStartHHMM] = React.useState("08:00");
  const [endHHMM, setEndHHMM] = React.useState("14:00");
  const [roleCode, setRoleCode] = React.useState("front_desk");
  const [notes, setNotes] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [validationError, setValidationError] = React.useState(null);

  // SCHED-SLICE1-K — Admin-set-availability sub-modal state. Opened from
  // the conflict-banner affordance "Set availability for [first name]"
  // when an availability conflict is active AND we have a `userId` for
  // the selected staff member. Pre-filled with the launching shift's
  // window per visual spec §10K.2.
  const [adminAvailModalOpen, setAdminAvailModalOpen] = React.useState(false);

  // Hydrate form on open.
  React.useEffect(() => {
    if (!open || !initialShift) return;
    setStaffProfileId(initialShift.staffProfileId ? String(initialShift.staffProfileId) : "");
    setDayYmd(initialShift.startTime ? formatDayYmd(initialShift.startTime) : formatDayYmd(new Date()));
    setStartHHMM(initialShift.startTime ? formatHHMM(initialShift.startTime) : "08:00");
    if (initialShift.endTime) {
      setEndHHMM(formatHHMM(initialShift.endTime));
    } else if (initialShift.startTime) {
      const e = moment
        .tz(initialShift.startTime, STUDIO_TZ)
        .add(DEFAULT_DURATION_MINUTES, "minutes");
      setEndHHMM(e.format("HH:mm"));
    } else {
      setEndHHMM("09:00");
    }
    setRoleCode(initialShift.roleCode || "front_desk");
    setNotes(initialShift.notes || "");
    setConfirmDelete(false);
    setValidationError(null);
  }, [open, initialShift]);

  // Esc closes (unless mid-submit).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !isSubmitting && typeof onClose === "function") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, isSubmitting]);
  const startIso = composeIso(dayYmd, startHHMM);
  const endIso = composeIso(dayYmd, endHHMM);
  // SCHED-SAME-ROLE-OVERLAP-WARN — soft-warning detection. Computed every
  // render (the underlying helper is pure + O(N); N = visible-week shifts,
  // typically 10-50). React's reconciliation handles the 300ms-feeling
  // debounce naturally — the modal re-renders when admin changes inputs,
  // and the helper bails fast when inputs are incomplete or invalid.
  const sameRoleOverlapDescriptors = React.useMemo(
    () =>
      detectSameRoleOverlapShifts({
        proposed: {
          startTime: startIso,
          endTime: endIso,
          roleCode,
        },
        editingShiftId: isEdit && initialShift ? initialShift._id : null,
        shifts,
        staffById,
      }),
    [startIso, endIso, roleCode, isEdit, initialShift, shifts, staffById]
  );
  const hasSameRoleOverlap = sameRoleOverlapDescriptors.length > 0;

  if (!open) return null;

  

  // FE-side HARD BLOCK availability pre-check — gates Save per visual spec §10.1.
  const availabilityConflict = staffProfileId
    ? preValidateAvailability({ staffProfileId, startIso, endIso, availability })
    : null;

  

  // SCHED-SLICE1-K — derive target staff identity for the admin-set
  // affordance. The BE returns `staff` with populated `userId` ({ _id,
  // firstName, lastName, ... }) per workforceShiftsController.listShifts.
  // Defense-in-depth: if `userId` is not an object (some legacy shapes
  // return it as an ObjectId string), the affordance hides itself rather
  // than firing a request with the wrong identity.
  const selectedStaffProfile = (Array.isArray(staff) ? staff : []).find(
    (p) => p && String(p._id) === String(staffProfileId)
  );
  const selectedStaffUser =
    selectedStaffProfile &&
    selectedStaffProfile.userId &&
    typeof selectedStaffProfile.userId === "object"
      ? selectedStaffProfile.userId
      : null;
  const selectedStaffUserId =
    selectedStaffUser && selectedStaffUser._id
      ? String(selectedStaffUser._id)
      : "";
  const selectedStaffFullName = selectedStaffProfile
    ? resolveStaffDisplayName(selectedStaffProfile)
    : "";
  const selectedStaffFirstName = selectedStaffFullName
    ? selectedStaffFullName.split(/\s+/)[0]
    : "this staff";

  const isPublished = isEdit && initialShift && initialShift.status === "published";

  const handleSave = () => {
    const result = validateShiftFormPayload({
      staffProfileId,
      startIso,
      endIso,
      roleCode,
    });
    if (!result.ok) {
      setValidationError(result);
      return;
    }
    // NEW-SHIFT-AVAILABILITY-PEEK (Sprint 9) — the FE-side availability check
    // is now an inline SOFT WARNING (yellow caution banner) per the new
    // story Scenario 6 + visual spec §4. The legacy SCHED-SLICE1-E HARD BLOCK
    // is retired; the BE-side SCHED-1B/1K conflict-detection guard rail
    // remains in place as defense-in-depth (AuditLog row on conflict-but-saved).
    // Admin is allowed to proceed past the warning.
    setValidationError(null);
    onSave({
      staffProfileId,
      startTime: startIso,
      endTime: endIso,
      roleCode,
      notes: notes.trim() || null,
    });
  };

  const handleDelete = () => {
    if (!isEdit || !initialShift || !initialShift._id) return;
    // PUBLISHED shifts: skip the in-modal tap-again confirm and hand off
    // to the cancel-with-reason sub-modal per SCHED-SLICE1-C AC §14.
    // The sub-modal handles its own confirmation + required reason capture.
    if (isPublished) {
      if (typeof onCancelPublished === "function") {
        onCancelPublished(initialShift);
      }
      return;
    }
    // DRAFT shifts: existing tap-again confirm + DELETE /shifts/:id path.
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    onDelete(initialShift._id);
  };

  // NEW-SHIFT-AVAILABILITY-PEEK — soft-warning posture: availability conflict
  // no longer disables Save (Scenario 6 + visual spec §4.2). Only in-flight
  // submission disables the button.
  const saveDisabled = isSubmitting;

  return (
    <>
      <div
        className="edit-shift-modal__backdrop"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
        data-testid="edit-shift-modal-backdrop"
      />
      <div
        className="edit-shift-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-shift-modal-heading"
        data-testid="edit-shift-modal"
      >
        <header className="edit-shift-modal__header">
          <h2
            id="edit-shift-modal-heading"
            className="edit-shift-modal__heading"
          >
            {isEdit ? "Edit shift" : "New shift"}
          </h2>
          <button
            type="button"
            className="edit-shift-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close shift modal"
            data-testid="edit-shift-modal-close"
          >
            &times;
          </button>
        </header>

        <div className="edit-shift-modal__body">
          {isPublished && (
            <div
              className="edit-shift-modal__publish-banner"
              role="note"
              data-testid="edit-shift-modal-publish-banner"
            >
              This shift is published. Saving changes will notify the affected staff
              member and record an audit entry.
            </div>
          )}

          {serverErrorMessage && (
            <div
              className="edit-shift-modal__error"
              role="alert"
              data-testid="edit-shift-modal-server-error"
            >
              {serverErrorMessage}
            </div>
          )}

          <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-staff">
            Staff
            <span className="edit-shift-modal__required" aria-hidden="true"> *</span>
          </label>
          <select
            id="edit-shift-modal-staff"
            className="edit-shift-modal__select"
            value={staffProfileId}
            onChange={(e) => setStaffProfileId(e.target.value)}
            disabled={isSubmitting}
            data-testid="edit-shift-modal-staff"
          >
            <option value="">Select staff member</option>
            {Array.isArray(staff) &&
              staff.map((p) => (
                <option key={p._id} value={p._id}>
                  {resolveStaffDisplayName(p)}
                  {p.primaryRole ? ` — ${formatRoleLabel(p.primaryRole)}` : ""}
                </option>
              ))}
          </select>

          {/* NEW-SHIFT-AVAILABILITY-PEEK — inline contextual mini-strip per
              Zuri Option C (docs/design/NEW-SHIFT-AVAILABILITY-PEEK-design.md).
              Surfaces the selected staff's availability windows for the
              currently-selected shift day BEFORE save, inverting the
              build-then-discover-conflict cycle. The strip is data-only
              against the in-prop `availability` array (loaded by
              ScheduleBuilder.fetchShifts for the visible week); no extra
              fetch is wired in v1. */}
          <AvailabilityPeekStrip
            staffProfileId={staffProfileId}
            staffProfile={selectedStaffProfile}
            dayYmd={dayYmd}
            availability={availability}
            onSetAvailabilityClick={
              selectedStaffUserId
                ? () => setAdminAvailModalOpen(true)
                : undefined
            }
          />

          <div className="edit-shift-modal__row">
            <div className="edit-shift-modal__field">
              <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-day">
                Day
                <span className="edit-shift-modal__required" aria-hidden="true"> *</span>
              </label>
              <select
                id="edit-shift-modal-day"
                className="edit-shift-modal__select"
                value={dayYmd}
                onChange={(e) => setDayYmd(e.target.value)}
                disabled={isSubmitting}
                data-testid="edit-shift-modal-day"
              >
                {Array.isArray(weekDays) &&
                  weekDays.map((d) => (
                    <option key={d.iso} value={d.iso}>
                      {d.weekday} {d.monthLabel} {d.dayLabel}
                    </option>
                  ))}
              </select>
            </div>
            <div className="edit-shift-modal__field">
              <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-start">
                Start time
                <span className="edit-shift-modal__required" aria-hidden="true"> *</span>
              </label>
              <select
                id="edit-shift-modal-start"
                className="edit-shift-modal__select"
                value={startHHMM}
                onChange={(e) => setStartHHMM(e.target.value)}
                disabled={isSubmitting}
                data-testid="edit-shift-modal-start"
              >
                {TIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="edit-shift-modal__field">
              <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-end">
                End time
                <span className="edit-shift-modal__required" aria-hidden="true"> *</span>
              </label>
              <select
                id="edit-shift-modal-end"
                className="edit-shift-modal__select"
                value={endHHMM}
                onChange={(e) => setEndHHMM(e.target.value)}
                disabled={isSubmitting}
                data-testid="edit-shift-modal-end"
              >
                {TIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-role">
            Role
            <span className="edit-shift-modal__required" aria-hidden="true"> *</span>
          </label>
          <select
            id="edit-shift-modal-role"
            className="edit-shift-modal__select"
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
            disabled={isSubmitting}
            data-testid="edit-shift-modal-role"
          >
            {Object.values(ROLE_COLOR_MAP).map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>

          <label className="edit-shift-modal__label" htmlFor="edit-shift-modal-notes">
            Notes (optional)
          </label>
          <textarea
            id="edit-shift-modal-notes"
            className="edit-shift-modal__textarea"
            rows={3}
            maxLength={240}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
            data-testid="edit-shift-modal-notes"
          />

          {availabilityConflict && (
            /* NEW-SHIFT-AVAILABILITY-PEEK — soft-warning banner per Zuri
               visual spec §4.2. Yellow caution (--status-on-break 12% tint
               with 4px left-accent stripe), informational; Save is NOT
               disabled. The legacy SCHED-1E HARD BLOCK is retired here;
               BE-side SCHED-1B/1K conflict-detection guard rail remains
               (AuditLog row + post-save grid banner). The "Set availability
               for [Staff]" pill (SCHED-1K affordance) is preserved so admin
               retains the fix-in-place path. */
            <div
              className="edit-shift-modal__avail-banner edit-shift-modal__avail-banner--soft"
              role="alert"
              aria-live="polite"
              data-testid="edit-shift-modal-avail-conflict"
            >
              <div className="edit-shift-modal__avail-banner-row">
                <span
                  className="edit-shift-modal__avail-banner-icon"
                  aria-hidden="true"
                >
                  ⚠
                </span>
                <div className="edit-shift-modal__avail-banner-text">
                  <p className="edit-shift-modal__avail-banner-msg">
                    {availabilityConflict.message}
                  </p>
                  <p className="edit-shift-modal__avail-banner-sub">
                    {`Proceed anyway — ${selectedStaffFirstName} will be notified.`}
                  </p>
                </div>
              </div>
              {/* SCHED-SLICE1-K — Affordance per visual spec §10K.1.
                  Outlined-teal pill sits inside the soft-warning banner;
                  hidden when we don't have a resolvable `userId` for the
                  selected staff (defense-in-depth: server requires the
                  path param + would 404 anyway). */}
              {selectedStaffUserId ? (
                <button
                  type="button"
                  className="edit-shift-modal__set-avail-pill"
                  onClick={() => setAdminAvailModalOpen(true)}
                  disabled={isSubmitting}
                  aria-label={`Set availability for ${selectedStaffFullName}`}
                  data-testid="edit-shift-modal-set-avail-pill"
                >
                  Set availability for {selectedStaffFirstName}
                </button>
              ) : null}
            </div>
          )}

          {hasSameRoleOverlap && (
            /* SCHED-SAME-ROLE-OVERLAP-WARN — soft yellow advisory banner per
               Zuri visual spec §3. Reuses PEEK §4 banner vocabulary verbatim
               (`.edit-shift-modal__avail-banner--soft` styling); only delta
               is the OTHER STAFF NAME bold emphasis. Banner stacks BELOW the
               PEEK §4 availability banner when both fire (visual spec §2 —
               12px gap; the helper class below tightens the top margin).
               Save is NEVER disabled — admin retains authority. */
            <SameRoleOverlapBanner
              descriptors={sameRoleOverlapDescriptors}
              proposedStaffProfile={selectedStaffProfile}
              proposedDayIso={dayYmd}
              proposedRoleCode={roleCode}
              isStackedBelowAvailBanner={Boolean(availabilityConflict)}
            />
          )}

          {validationError && !validationError.ok && (
            <div
              className="edit-shift-modal__validation"
              role="alert"
              data-testid="edit-shift-modal-validation"
            >
              {validationError.message}
            </div>
          )}

          <p className="edit-shift-modal__tz-note" aria-hidden="true">
            All times in {STUDIO_TZ_LABEL} ({STUDIO_TZ}).
          </p>
        </div>

        <footer className="edit-shift-modal__footer">
          {isEdit && (
            <button
              type="button"
              className={
                confirmDelete && !isPublished
                  ? "edit-shift-modal__delete edit-shift-modal__delete--confirm"
                  : "edit-shift-modal__delete"
              }
              onClick={handleDelete}
              disabled={isSubmitting}
              data-testid="edit-shift-modal-delete"
            >
              {isPublished
                ? "Delete shift"
                : confirmDelete
                  ? "Tap again to confirm delete"
                  : "Delete shift"}
            </button>
          )}
          <div className="edit-shift-modal__footer-spacer" />
          <button
            type="button"
            className="edit-shift-modal__cancel"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="edit-shift-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="edit-shift-modal__save"
            onClick={handleSave}
            disabled={saveDisabled}
            data-testid="edit-shift-modal-save"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </footer>
      </div>

      {/* SCHED-SLICE1-K — Admin-set-availability sub-modal. Stacks on top of
          this modal; admin context (Schedule Builder, ADMIN MODE chrome) is
          preserved end-to-end. Pre-fill is the launching shift's day/window
          per visual spec §10K.2. */}
      <AdminSetAvailabilityModal
        open={adminAvailModalOpen}
        userId={selectedStaffUserId}
        staffFullName={selectedStaffFullName}
        prefill={
          adminAvailModalOpen && availabilityConflict
            ? {
                dayOfWeek: moment.tz(startIso, STUDIO_TZ).day(),
                startTime: startHHMM,
                endTime: endHHMM,
              }
            : null
        }
        onClose={() => setAdminAvailModalOpen(false)}
        onAvailabilityChanged={onAvailabilityChanged}
      />
    </>
  );
};

export default EditShiftModal;
